import { autorun, comparer, type IReactionDisposer } from 'mobx'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { reportPhotoBookDbFailure } from 'services/PhotoBook/photoBookDbFailure'
import type { StoredPhotoBook } from 'services/PhotoBook/photoBookSaver.types'
import type { PhotoBookStorageBackend } from 'services/PhotoBook/photoBookStorageBackend.types'
import { photosInPages } from 'services/PhotoBook/photoBookUtils'
import { deepToJS } from 'utils/mobx/deepToJS'

const PERSIST_DELAY_MS = 150

/**
 * Owns the stored record holding one book, in whichever backend the parent's
 * load settled on. Writes it, clears it.
 *
 * The autorun writes whatever the reader hands it, and the reader returns null
 * while writes are frozen — during editor init the book is rebuilt against the
 * photo bank, and a half-rebuilt book must not reach storage.
 */
export class PhotoBookStoragePersister {
  private readonly bookId: string

  private readonly getBookToWrite: () => StoredPhotoBook | null

  // Resolves once the parent's load has picked the backend. Every write and
  // delete chains on it, so they land in the order they were issued.
  private readonly storageBackendPromise: Promise<PhotoBookStorageBackend>

  private disposers: IReactionDisposer[] = []

  // What the record holds, as it was last loaded or written. The persisting
  // autorun fires once per page load whether anything was edited or not, and
  // opening a transaction per book on every boot is what this skips.
  private lastWrittenBook: StoredPhotoBook | null = null

  // A full localStorage stays full for the page, and the persisting autorun
  // retries every edit, so the report and the event go out once.
  private hasReportedFullStorage = false

  constructor(
    bookId: string,
    getBookToWrite: () => StoredPhotoBook | null,
    storageBackendPromise: Promise<PhotoBookStorageBackend>
  ) {
    this.bookId = bookId
    this.getBookToWrite = getBookToWrite
    this.storageBackendPromise = storageBackendPromise
  }

  // Seeds the skip-unchanged baseline with the record the parent's load handed
  // this store, so booting writes nothing back.
  markBookLoaded(book: StoredPhotoBook): void {
    this.lastWrittenBook = book
  }

  // The snapshot detaches the stored record from the live observable book, and —
  // because the persisting autorun runs it before the unchanged write is
  // skipped — it subscribes that autorun to every leaf, so in-place edits
  // anywhere in the book re-fire the write. Resolves false when the write did
  // not land (the quota rejects it), never rejects.
  writeBook(book: StoredPhotoBook): Promise<boolean> {
    const plainBook = deepToJS(book)
    if (comparer.structural(plainBook, this.lastWrittenBook)) {
      return Promise.resolve(true)
    }

    this.lastWrittenBook = plainBook
    return this.storageBackendPromise.then((storageBackend) =>
      storageBackend.putBook(plainBook).then(
        () => true,
        (err) => {
          this.lastWrittenBook = null
          if (!this.hasReportedFullStorage) {
            this.reportWriteFailure(err, storageBackend, plainBook)
          }
          return false
        }
      )
    )
  }

  clearStoredBook(): void {
    this.lastWrittenBook = null
    this.storageBackendPromise.then((storageBackend) =>
      storageBackend.deleteBook(this.bookId).catch((err) => {
        reportPhotoBookDbFailure('delete', err, {
          bookId: this.bookId,
          storageBackend: storageBackend.name,
        })
      })
    )
  }

  // Watching is client-only: the autorun reaches browser storage.
  startPersisting(): void {
    this.disposers.push(
      autorun(
        () => {
          const book = this.getBookToWrite()
          if (!book) {
            return
          }

          // Fire-and-forget: writeBook never rejects and logs its own failures.
          this.writeBook(book)
        },
        {
          name: 'PhotoBookStoragePersister:writeBook',
          delay: PERSIST_DELAY_MS,
        }
      )
    )
  }

  dispose(): void {
    this.disposers.forEach((dispose) => dispose())
    this.disposers = []
  }

  private reportWriteFailure(
    err: unknown,
    storageBackend: PhotoBookStorageBackend,
    book: StoredPhotoBook
  ): void {
    const { failureKind } = reportPhotoBookDbFailure('persist', err, {
      bookId: this.bookId,
      storageBackend: storageBackend.name,
    })
    if (failureKind !== 'storage-full') {
      return
    }

    this.hasReportedFullStorage = true
    analytics.track(EVENT_NAMES.BOOKS_STORAGE_FULL, {
      'Book ID': this.bookId,
      'Page Count': book.pages.length,
      'Photo Count': photosInPages(book.pages).length,
    })
  }
}
